import * as React from 'react';
import {FormHelperText, FormLabel, Box, FormControl, TextField as TextEl} from '@mui/material';
import { useMemo, useState, useEffect } from 'react';

interface NumberProps {
    variant?: 'outlined' | 'filled' | 'standard';
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    label?: string;
    description?: string;
    required?: boolean;
    value: number | string | null;
    error?: string;
    minimum?: number;
    maximum?: number;
    enabled?: boolean;
    visible?: boolean;
    appValidated?: boolean;
}

const Number = React.memo((props: NumberProps) => {
    const {
        variant = 'outlined',
        onChange,
        label,
        description,
        required = false,
        value,
        error,
        minimum,
        maximum,
        enabled = true,
        visible = true,
        appValidated = false
    } = props;

    const [appHasBeenValidated, setAppHasBeenValidated] = useState(false);

    React.useEffect(() => {
        if (appValidated) {
            setAppHasBeenValidated(true);
        }
    }, [appValidated]);

    // Memoize computed values
    const showDescription = useMemo(() =>
            description !== undefined && description !== "",
        [description]
    );

    const inputProps = useMemo(() => {
        const props: any = {};
        if (minimum !== undefined) props.min = minimum;
        if (maximum !== undefined) props.max = maximum;
        return props;
    }, [minimum, maximum]);

    const isEmpty = useMemo(() => value === null || value === '' || value === undefined, [value]);

    const showRequiredError = useMemo(() =>
            required && appHasBeenValidated && isEmpty,
        [required, appHasBeenValidated, isEmpty]
    );

    function doRangeValidation(numericValue: number, computedError: string | undefined) {
        if (minimum !== undefined && maximum !== undefined) {
            if (numericValue < minimum || numericValue > maximum) {
                computedError = `Must be between ${minimum} and ${maximum}`;
            }
        } else if (minimum !== undefined && numericValue < minimum) {
            computedError = `Must be at least ${minimum}`;
        } else if (maximum !== undefined && numericValue > maximum) {
            computedError = `Must be at most ${maximum}`;
        }
        return computedError;
    }

// Memoize error calculation - show errors only after submission (now persistent via appHasBeenValidated)
    const { computedError, showError } = useMemo(() => {
        // Only show errors after submission (persistent)
        if (!appHasBeenValidated && isEmpty) return {
            computedError: '',
            showError: false
        };

        let computedError = error;

        // Required field validation (now using showRequiredError for precision)
        if (showRequiredError) {
            computedError = 'This field is required';
        }
        // Numeric validation
        else if (!isEmpty && typeof value !== 'number') {
            const numericValue = value ? parseFloat(value as string) : NaN;
            const isNumber = !isNaN(numericValue);
            if (!isNumber) {
                computedError = 'Please enter a valid number';
            } else {
                computedError = doRangeValidation(numericValue, computedError);
            }
        }
        // Range validation
        else {
            const numericValue = typeof value === 'number' ? value : value ? parseFloat(value as string) : NaN;
            const isNumber = !isNaN(numericValue);
            if (isNumber) {
                computedError = doRangeValidation(numericValue, computedError);
            }
        }

        const showError = computedError !== undefined && computedError !== "";
        return { computedError: computedError || '', showError };
    }, [error, value, minimum, maximum, required, appHasBeenValidated, showRequiredError, isEmpty]);

    // Early return for hidden components
    if (!visible) return null;

    return (
        <Box sx={{ display: 'block' }}>
            <FormControl className="form-control" fullWidth={true}>
                <FormLabel required={required} color="secondary" className="form-separate-label">
                    {label}
                </FormLabel>
                <TextEl
                    error={showError}
                    required={required}
                    value={value ?? ''}
                    onChange={onChange}
                    InputProps={{ inputProps }}
                    type='number'
                    variant={variant}
                    className='form-control-field'
                    disabled={!enabled}
                    sx={{
                        ".Mui-disabled": { opacity: 0.6 },
                        "input[type=number]": {
                            MozAppearance: 'textfield',
                            "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                                WebkitAppearance: 'none',
                                margin: 0
                            }
                        }
                    }}
                    fullWidth
                />
                {showDescription && (
                    <FormHelperText>
                        {description}
                    </FormHelperText>
                )}
                {showError && (
                    <FormHelperText error={true}>
                        {computedError}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    );
});

Number.displayName = 'Number';

export default Number;
